import {
  INITIAL_BALANCE,
  INITIAL_BET,
  MIN_VISIBLE_WORLD_HEIGHT,
  MIN_VISIBLE_WORLD_WIDTH,
  SIM_DT,
  Round,
  clamp,
  commit,
} from "@benji/sim-core";
export type { GameState } from "@benji/sim-core";
import type { ServerMessage } from "@benji/sim-core";
import { GameScene } from "./GameScene";
import { NetClient, playerToken, randomClientSeed } from "../net/NetClient";
import type { UIOverlay } from "../ui/UIOverlay";

// Bet rungs the in-game -/+ steppers cycle through (mirrors the home presets);
// the top rung is always MAX (= whole balance), appended dynamically.
const BET_PRESETS = [1, 5, 10, 20, 50];

// Cap catch-up steps so a long frame stall can't cause a spiral of death.
const MAX_STEPS_PER_FRAME = 8;

/**
 * Client orchestrator.
 *
 * The deterministic logic + physics live in {@link Round} (`@benji/sim-core`),
 * the exact code the authoritative server runs. When connected, the server is
 * the source of truth for the wallet/outcome and supplies provably-fair seeds;
 * the client runs a local Round on those seeds purely as smooth prediction and
 * sends inputs addressed by sim tick. With no server it degrades gracefully to
 * a fully local game (random seeds, client-side wallet).
 *
 * The Round always runs at the fixed logical viewport (= the server's), so the
 * simulation is device-independent; the camera scales it to the real screen.
 */
export class Game {
  private scene = new GameScene();
  private ui?: UIOverlay;
  private round!: Round;

  private net = new NetClient();
  private online = false;
  private roundId: string | null = null;
  private clientSeed = randomClientSeed();
  private awaitingServerStart = false;

  private currentBet = INITIAL_BET;
  private balance = INITIAL_BALANCE;
  private staked = false; // local (offline) bet debited for the active round
  private creditedRound = false; // local (offline) payout credited
  private accumulator = 0;

  async init(host: HTMLElement) {
    await this.scene.init(host, (dt) => this.frame(dt));
    this.newRound();
    this.net.onMessage((m) => this.onServer(m));
    this.net.onOpen(() => this.net.send({ t: "auth", token: playerToken() }));
    this.net.onClose(() => { this.online = false; }); // keep playing offline
    this.net.connect();
  }

  bindUI(ui: UIOverlay) {
    this.ui = ui;
    ui.setBalance(this.balance);
    ui.setBet(this.currentBet);
    this.refreshUI();
  }

  resize() {
    // Only the camera follows the screen; the sim viewport stays fixed so the
    // round is reproducible on any device (and matches the server).
    this.scene.resize();
  }

  getBalance(): number { return this.balance; }
  getBet(): number { return this.currentBet; }
  getTextures(): Record<string, import("pixi.js").Texture> { return this.scene.textures; }

  setBet(bet: number) {
    if (!this.canEditBet()) return;
    this.currentBet = clamp(Math.floor(bet), 1, this.maxBet());
    this.round.setBet(this.currentBet);
    this.ui?.setBet(this.currentBet);
  }

  // Bet may be edited only while it hasn't been staked yet: before the first
  // jump of a round, or after a round has ended (between rounds).
  canEditBet(): boolean {
    if (this.awaitingServerStart) return false;
    const s = this.round.state;
    return (
      s === "idle" ||
      (s === "waiting" && this.round.step === 0) ||
      s === "cashed" ||
      s === "crashed"
    );
  }

  private maxBet(): number {
    return Math.max(1, Math.floor(this.balance));
  }

  private betLadder(): number[] {
    const cap = this.maxBet();
    return [...BET_PRESETS.filter((v) => v < cap), cap];
  }

  betCanDecrease(): boolean {
    return this.canEditBet() && this.betLadder().some((v) => v < this.currentBet);
  }

  betCanIncrease(): boolean {
    return this.canEditBet() && this.betLadder().some((v) => v > this.currentBet);
  }

  cycleBet(dir: number) {
    if (!this.canEditBet()) return;
    const ladder = this.betLadder();
    const cur = clamp(this.currentBet, 1, this.maxBet());
    const target = dir < 0
      ? ladder.reduce<number | undefined>((best, v) => (v < cur ? v : best), undefined)
      : ladder.find((v) => v > cur);
    if (target === undefined || target === this.currentBet) return;
    this.currentBet = target;
    this.round.setBet(this.currentBet);
    this.ui?.setBet(this.currentBet);
    this.refreshUI();
  }

  startRound() {
    if (this.awaitingServerStart) return;
    if (this.round.state !== "idle") return;
    if (this.currentBet > this.balance || this.currentBet <= 0) return;

    if (this.online && this.net.isOpen) {
      // Server is authoritative: ask it to open the round; we build the
      // prediction Round when `round.started` returns the derived seeds.
      this.clientSeed = randomClientSeed();
      this.awaitingServerStart = true;
      this.net.send({ t: "round.start", bet: this.currentBet, clientSeed: this.clientSeed });
      this.refreshUI();
      return;
    }

    // Offline: local-only round.
    this.round.setBet(this.currentBet);
    this.staked = false;
    this.round.startRound();
    this.refreshUI();
  }

  jump() {
    if (this.round.state !== "waiting") return;
    const wasLocked = this.round.isBetLocked;
    this.round.jump();
    if (this.online && this.roundId) {
      this.net.send({ t: "input.jump", roundId: this.roundId, tick: this.round.getTick() });
    } else if (!wasLocked && this.round.isBetLocked && !this.staked) {
      // Offline wallet: debit the stake on the first jump.
      this.balance -= this.round.result().bet;
      this.staked = true;
      this.ui?.setBalance(this.balance);
    }
    this.refreshUI();
  }

  cashOut(): boolean {
    const ok = this.round.cashOut();
    if (ok && this.online && this.roundId) {
      this.net.send({ t: "input.cashout", roundId: this.roundId, tick: this.round.getTick() });
    }
    return ok;
  }

  private onTryAgainCb?: () => void;
  onTryAgainRequested(cb: () => void) { this.onTryAgainCb = cb; }

  tryAgain() {
    if (this.round.state !== "crashed" && this.round.state !== "cashed") return;
    this.newRound();
    if (this.currentBet > this.balance || this.currentBet <= 0) {
      this.onTryAgainCb?.();
      return;
    }
    this.startRound();
  }

  // ---- server messages -----------------------------------------------------
  private onServer(m: ServerMessage) {
    switch (m.t) {
      case "auth.ok":
        this.online = true;
        this.balance = m.balance;
        this.ui?.setBalance(this.balance);
        this.refreshUI();
        break;
      case "round.started":
        this.roundId = m.roundId;
        this.serverSeedHash = m.serverSeedHash;
        this.awaitingServerStart = false;
        this.bindRound(m.levelSeed, m.fxSeed);
        this.round.startRound();
        this.refreshUI();
        break;
      case "round.ended":
        // Server is authoritative for the wallet and the result.
        this.balance = m.balance;
        this.creditedRound = true; // suppress any offline crediting
        this.ui?.setBalance(this.balance);
        if (commit(m.reveal.serverSeed) !== m.reveal.serverSeedHash) {
          console.warn("[net] provably-fair commit mismatch on reveal");
        }
        this.roundId = null;
        break;
      case "error":
        console.warn(`[net] ${m.code}: ${m.message}`);
        if (m.code === "insufficient_funds" || m.code === "bad_request") {
          this.awaitingServerStart = false;
          this.refreshUI();
        }
        break;
      // state.sync: reconciliation hook (seeds+inputs are deterministic, so
      // the prediction matches; divergence handling lands in a later pass).
    }
  }

  private serverSeedHash: string | null = null;

  // ---- round lifecycle -----------------------------------------------------
  private newRound() {
    // Idle scene uses random seeds; a networked round rebinds to server seeds.
    this.bindRound((Math.random() * 0xffffffff) >>> 0, (Math.random() * 0xffffffff) >>> 0);
  }

  private bindRound(levelSeed: number, fxSeed: number) {
    this.round = new Round({
      bet: this.currentBet,
      levelSeed,
      fxSeed,
      viewportW: MIN_VISIBLE_WORLD_WIDTH,
      viewportH: MIN_VISIBLE_WORLD_HEIGHT,
    });
    this.staked = false;
    this.creditedRound = false;
    this.scene.resetCamera();
    this.scene.syncToRound(this.round, 1, 0);
    this.refreshUI();
  }

  private frame(dt: number) {
    this.accumulator += dt;
    let steps = 0;
    while (this.accumulator >= SIM_DT && steps < MAX_STEPS_PER_FRAME) {
      this.round.tick();
      this.accumulator -= SIM_DT;
      steps++;
    }
    if (steps === MAX_STEPS_PER_FRAME) this.accumulator = 0;

    // Offline only: the client-side wallet. When online the balance is set
    // exclusively by the server (auth.ok / round.ended).
    if (!this.online && this.round.outcome === "cashed" && !this.creditedRound) {
      this.balance += this.round.result().payout;
      this.creditedRound = true;
      this.ui?.setBalance(this.balance);
    }

    for (const e of this.round.drainEvents()) {
      const p = this.scene.screenPoint(e.x, e.y);
      this.scene.particles.emitLeaves(p.x, p.y, e.count);
    }

    // Leftover fixed-step fraction → smooth render interpolation at any
    // display refresh rate (fixes the fixed-step temporal aliasing "jerks").
    const alpha = Math.min(1, Math.max(0, this.accumulator / SIM_DT));
    const b = this.round.benji;
    const bx = b.rPrevX + (b.worldX - b.rPrevX) * alpha;

    this.refreshUI();
    this.scene.syncToRound(this.round, alpha, dt);
    this.scene.updateCamera(dt, this.round.state, bx);
    this.scene.updateAtmosphere(dt, this.round.state);
  }

  private refreshUI() {
    // Win/secured labels must reflect the bet actually staked for the round,
    // not a freshly edited one (bet can be changed between rounds).
    const s = this.round.state;
    const betForRound =
      s === "idle" || (s === "waiting" && this.round.step === 0)
        ? this.currentBet
        : this.round.result().bet;
    this.ui?.refreshRound(s, this.round.step, this.round.multiplier, betForRound);
  }
}
